import { GRID_DETAIL_PANEL_TOGGLE_FIELD, GridColumnHeaders } from "@mui/x-data-grid-pro";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DiagnosticAddinData } from "../models/DiagnosticAddinData";
import { DiagnosticData } from "../models/DiagnosticData";
import { NameCell } from "./grid/cells/NameCell";
import { DiagnosticOverrideDetail } from "./grid/DiagnosticOverrideDetail";
import { NoRowsOverlay } from "./grid/NoRowsOverlay";
import { I18nGrid } from "@/components/data/I18nGrid";
import { PriorityCell } from "@/components/grid/cells/PriorityCell";
import AddInIds from "@/constants/AddInIds";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";

interface Props {
  data?: DiagnosticData[];
  isLoading: boolean;
}

export function DiagnosticsGrid({ data, isLoading }: Props) {
  const { t } = useTranslation();
  const updateAddin = useUpdateAddIn<DiagnosticAddinData>(AddInIds.diagnosticData, [
    "diagnosticId",
    "groupId",
  ]);

  const getDetailPanelContent = useCallback(
    ({ row }: { row: DiagnosticData }) => (
      <DiagnosticOverrideDetail diagnosticId={row.id} />
    ),
    [],
  );
  const getDetailPanelHeight = useCallback(() => "auto" as const, []);
  return (
    <>
      <I18nGrid
        settingsKey="diagnosticEdit"
        sx={{ flexGrow: 1, "& .MuiDataGrid-detailPanel": { overflow: "visible" } }}
        rows={data ?? []}
        getRowId={row => row.id}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        pinnedColumns={{ left: [GRID_DETAIL_PANEL_TOGGLE_FIELD] }}
        slots={{
          noRowsOverlay: NoRowsOverlay,
          columnHeaders: GridColumnHeaders,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
        }}
        columns={[
          {
            flex: 1,
            minWidth: 200,

            field: "source",
            headerName: t("diagnosticEdit.source", "Source"),
            renderCell: NameCell,
          },
          {
            flex: 1,
            minWidth: 200,

            field: "name",
            headerName: t("diagnosticEdit.name", "Name"),
            renderCell: NameCell,
          },
          {
            flex: 1,
            minWidth: 200,

            field: "priority",
            headerName: t("diagnosticEdit.priotity", "Priority"),
            renderCell: PriorityCell,
            type: "singleSelect",
            valueOptions: [
              { value: 0, label: "N/A" },
              { value: 1, label: "Info" },
              { value: 2, label: "Low" },
              { value: 3, label: "Medium" },
              { value: 4, label: "High" },
            ],
            editable: true,
          },
          {
            flex: 1,
            minWidth: 200,
            field: "description",
            headerName: t("diagnosticEdit.description", "Description"),
            renderCell: NameCell,
            editable: true,
          },
          {
            flex: 1,
            minWidth: 200,
            field: "instruction",
            headerName: t("faultOverview.instruction", "Instruction"),
            renderCell: NameCell,
            editable: true,
          },
        ]}
        processRowUpdate={async (updatedRow, _) => {
          updateAddin.mutate({
            diagnosticId: updatedRow.id,
            instruction: updatedRow.instruction,
            priority: updatedRow.priority,
            description: updatedRow.description,
          });
          return updatedRow;
        }}
        onProcessRowUpdateError={error => console.error(error)}
        loading={isLoading}
        initialState={{
          sorting: {
            sortModel: [{ field: "description", sort: "desc" }],
          },
        }}
      />
    </>
  );
}
